import { isValidEmail } from '@theme/email-validation';

const PHONE_MAX_DIGITS = 11;
const BRAZIL_COUNTRY_CODE = '55';

/**
 * Keeps only the digits of a phone number, dropping a leading country code (+55)
 * when it comes from a pasted or pre-filled value, and caps it at DDD + 9 digits.
 * @param {string} value
 * @returns {string}
 */
function getPhoneDigits(value) {
  let digits = value.replace(/\D/g, '');

  if (digits.length > PHONE_MAX_DIGITS && digits.startsWith(BRAZIL_COUNTRY_CODE)) {
    digits = digits.slice(BRAZIL_COUNTRY_CODE.length);
  }

  return digits.slice(0, PHONE_MAX_DIGITS);
}

/**
 * Formats digits as a Brazilian phone number: (11) 3333-4444 (landline) or (11) 99999-4444 (mobile).
 * Separators are only added once a digit follows them, so backspace never gets stuck on one.
 * @param {string} digits
 * @returns {string}
 */
function formatPhone(digits) {
  if (digits.length <= 2) return digits ? `(${digits}` : '';

  const area = digits.slice(0, 2);
  if (digits.length <= 6) return `(${area}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${area}) ${digits.slice(2, 6)}-${digits.slice(6)}`;

  return `(${area}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Accepts landlines (10 digits) and mobiles (11 digits, starting with 9) with a valid area code.
 * @param {string} digits
 * @returns {boolean}
 */
function isValidPhone(digits) {
  if (digits.length !== 10 && digits.length !== 11) return false;
  if (!/^[1-9][1-9]/.test(digits)) return false;

  return digits.length === 11 ? digits[2] === '9' : /[2-9]/.test(digits[2] ?? '');
}

/**
 * Contact Form Validation Component
 *
 * Validates the contact form on the client before it is submitted and shows an
 * inline error message under each invalid field. Fields opt in with
 * `data-validate="name|email|phone|body"`; `required` marks the mandatory ones.
 * Error messages come from `data-error-<field>-<kind>` attributes on the element.
 */
class ContactFormValidation extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLFormElement | null} */
    this.form = null;
    /** @type {Map<HTMLInputElement | HTMLTextAreaElement, string | null>} */
    this.originalDescribedBy = new Map();

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    for (const field of this.getFields()) {
      this.originalDescribedBy.set(field, field.getAttribute('aria-describedby'));
      if (field.dataset.validate === 'phone') this.applyPhoneMask(/** @type {HTMLInputElement} */ (field));
    }

    this.form.addEventListener('submit', this.handleSubmit);
    this.form.addEventListener('input', this.handleInput);
    this.form.addEventListener('focusout', this.handleFocusOut);

    // Only turn off native validation once this component has taken over, so the form
    // still validates natively if it is submitted before the script loads.
    this.form.noValidate = true;
  }

  disconnectedCallback() {
    if (!this.form) return;

    this.form.removeEventListener('submit', this.handleSubmit);
    this.form.removeEventListener('input', this.handleInput);
    this.form.removeEventListener('focusout', this.handleFocusOut);
    this.form.noValidate = false;
  }

  /**
   * @returns {Array<HTMLInputElement | HTMLTextAreaElement>}
   */
  getFields() {
    return this.form ? Array.from(this.form.querySelectorAll('[data-validate]')) : [];
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {'required' | 'invalid' | null} The validation error, or null when the value is valid.
   */
  getError(field) {
    const value = field.value.trim();

    if (value === '') return field.required ? 'required' : null;

    if (field.dataset.validate === 'email') return isValidEmail(value) ? null : 'invalid';

    if (field.dataset.validate === 'phone') return isValidPhone(getPhoneDigits(value)) ? null : 'invalid';

    return null;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {'required' | 'invalid' | null} error
   */
  showError(field, error) {
    const errorElement = document.getElementById(`${field.id}-client-error`);
    const errorText = errorElement && errorElement.querySelector('.contact-form__field-error-text');
    if (!errorElement || !errorText) return;

    if (error) {
      const key = `error${capitalize(field.dataset.validate)}${capitalize(error)}`;

      errorText.textContent = this.dataset[key] || '';
      errorElement.removeAttribute('hidden');
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', errorElement.id);
    } else {
      errorText.textContent = '';
      errorElement.setAttribute('hidden', '');
      field.removeAttribute('aria-invalid');

      const original = this.originalDescribedBy.get(field);
      if (original) {
        field.setAttribute('aria-describedby', original);
      } else {
        field.removeAttribute('aria-describedby');
      }
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    /** @type {HTMLInputElement | HTMLTextAreaElement | undefined} */
    let firstInvalid;

    for (const field of this.getFields()) {
      field.value = field.value.trim();

      const error = this.getError(field);
      this.showError(field, error);

      if (error && !firstInvalid) firstInvalid = field;
    }

    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
    }
  }

  /**
   * Clears a field's error as soon as it becomes valid; never shows one while typing.
   * @param {Event} event
   */
  handleInput(event) {
    const field = this.asField(event.target);
    if (!field) return;

    if (field.dataset.validate === 'phone') this.applyPhoneMask(/** @type {HTMLInputElement} */ (field));
    if (field.hasAttribute('aria-invalid') && !this.getError(field)) this.showError(field, null);
  }

  /**
   * Formats the field value as a phone number, keeping the caret next to the same digit.
   * @param {HTMLInputElement} field
   */
  applyPhoneMask(field) {
    const raw = field.value;
    const caret = field.selectionStart ?? raw.length;
    const formatted = formatPhone(getPhoneDigits(raw));

    if (formatted === raw) return;

    field.value = formatted;

    if (document.activeElement !== field || caret >= raw.length) return;

    // Move the caret after the same number of digits it followed before formatting.
    let digitsBeforeCaret = raw.slice(0, caret).replace(/\D/g, '').length;
    let position = 0;

    while (position < formatted.length && digitsBeforeCaret > 0) {
      if (/\d/.test(formatted.charAt(position))) digitsBeforeCaret--;
      position++;
    }

    field.setSelectionRange(position, position);
  }

  /**
   * Validates a field when leaving it, unless it is still empty.
   * @param {FocusEvent} event
   */
  handleFocusOut(event) {
    const field = this.asField(event.target);
    if (field && field.value.trim() !== '') this.showError(field, this.getError(field));
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLInputElement | HTMLTextAreaElement | null}
   */
  asField(target) {
    return target instanceof HTMLElement && target.matches('[data-validate]')
      ? /** @type {HTMLInputElement | HTMLTextAreaElement} */ (target)
      : null;
  }
}

/**
 * @param {string | undefined} value
 * @returns {string}
 */
function capitalize(value = '') {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

if (!customElements.get('contact-form-validation')) {
  customElements.define('contact-form-validation', ContactFormValidation);
}
